import './styles.scss';

import { Datetime, createNotification, parseObj, pascalCase, printScreen, saveFile, stringify, uuid } from '@/utils';
import EvaluationSummaryModal, { useEvaluationSummaryModal } from '@/pages/Reports/DynamicReport/components/EvaluationSummaryModal';
import { reportQueryAsCsv, reportQueryInputKeys, useReportQuery } from '@/api/services/reports/reportQuery';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Alert } from 'react-bootstrap';
import { DATE_INPUT_FORMAT } from '@/constants';
import DetailsDrawer from '@/pages/Reports/DynamicReport/components/DetailsDrawer';
import Filters from '@/components/Filters';
import { LoadingBlur } from '@/components/LoadingSpinner';
import { Middle } from '@/components/Align';
import PdfWorkerClass from '@/workers/pdf.worker';
import ReportDisplay from '@/pages/Reports/DynamicReport/components/ReportDisplay';
import { ReportTablePDFProps } from './components/ReportTablePdf';
import ReportTypeDropdown from '@/pages/Reports/DynamicReport/components/ReportTypeDropdown';
import SidebarButton from '@/components/SidebarButton';
import SnapshotsModal from '@/components/SnapshotsModal';
import { Toast } from '@/models';
import { generateAndDownloadTrainingReportPDF } from '@/pages/Reports/api/services/generateTrainingReportPDF';
import reportFilters from '@/pages/Reports/DynamicReport/filters';
import reportTypes from '@/pages/Reports/DynamicReport/reportTypes';
import { useParams } from 'react-router-dom';
import { wrap } from 'comlink';

// This prevents an error when executing the worker by tricking
// the type system into allowing it to be executable via being any
// TODO: Find a better way to do this
const PdfWorker = PdfWorkerClass as any;

type PdfApi = { renderPdf: (p: ReportTablePDFProps) => Promise<ArrayBuffer> };

type DynamicReportState = {
  details: Record<string, unknown>;
  display: ReportDisplayEnum;
  showSnapshots?: boolean;
};
type DynamicReportProps = {
  report?: string;
  onEmpty?: (props) => JSX.Element;
  widget?: boolean;
};
export enum ReportDisplayEnum {
  TABLE = 'Table',
  CHART = 'Chart',
}
const initDynamicReportState = {
  details: undefined,
  display: ReportDisplayEnum.TABLE,
};

export type ReportTypeKeys = keyof typeof reportTypes;

const allowedPDFReports: Partial<ReportTypeKeys>[] = [
  'fcrReport',
  'clientSummaryReport',
  'completionReport',
  'ratingsReport',
  'vehicleReport',
  'tripIssueReport',
  'kpiReport',
  'onTimePerformanceReport',
  'drivetimeReport',
];

const NoContent = (): JSX.Element => (
  <h3 className="text-gray-subtle text-center">
    <i className="sv sv-chart-growth fa-2x" />
    <br />
    Nothing to Report
  </h3>
);
export const useParsedObj = (input) => {
  const lastInput = useRef({});
  const [result, setResult] = useState({ filters: {}, header: undefined, footer: undefined, tables: [], key: uuid() });
  if (Object.keys(input || {}).length === 0 || stringify.compare(lastInput.current, input)) return result;
  lastInput.current = input || {};
  const res = parseObj(input);
  const { filters = {}, header, footer, tables = [] } = res;
  const key = uuid();
  setResult({ filters, header, footer, tables, key });
  return result;
};

export function usePdfApi() {
  const worker = useMemo(() => new PdfWorker(), []);
  useEffect(() => () => worker.terminate(), [worker]);
  const api = useMemo(() => wrap<PdfApi>(worker), [worker]);
  return api;
}

const getReportOrientation = (reportType: ReportTypeKeys, data?: any): 'portrait' | 'landscape' => {
  switch (reportType) {
    case 'fcrReport':
      return data?.filters?.['fcrReportedBy']?.value?.length <= 2 ? 'portrait' : 'landscape';
    case 'clientSummaryReport':
    case 'completionReport':
    case 'ratingsReport':
    case 'vehicleReport':
    case 'tripIssueReport':
      return 'landscape';
    default:
      return 'landscape';
  }
};

const DynamicReport = ({ report, onEmpty: OnEmpty = NoContent, widget }: DynamicReportProps): JSX.Element => {
  const [state, setState] = useState<DynamicReportState>(initDynamicReportState);
  const [processingPDF, setProcessingPDF] = useState(false);
  const { details, display, showSnapshots } = state;
  const openEvaluation = useEvaluationSummaryModal(({ setState }) => setState);
  const { type = Object.keys(reportTypes)?.[0], id } = useParams();
  const reportType = (report || type) === 'complaintReport' ? 'fcrReport' : report || type;
  const [{ loading, data: { output = undefined, error: gqlError = undefined } = {}, error: apiError = undefined }, { fetch, refetch }] =
    useReportQuery(reportType);
  // TODO: Do not keep this, refactor it better for future errors
  let error = gqlError || apiError;
  if (error?.extensions?.code === 1002) {
    error = undefined;
  }
  const { filters, header, footer, tables, key } = useParsedObj(output);
  if (header?.['Zero Rates'])
    header['Zero Rates'].description = "Number of trips that have not been assigned a rate because a rate doesn't exist in the system";
  const lastQuery = useRef({});
  const lastReportType = useRef('');
  const isWidget = widget !== undefined && widget !== false;
  const api = usePdfApi();

  const pdfOrientation = getReportOrientation(reportType as ReportTypeKeys, { filters, tables });

  const handleDownloadReportPDF = useCallback(async () => {
    const timer = setTimeout(() => {
      createNotification(
        'Please wait the pdf is being generated. Depending on the size of the report, this may take a few minutes.',
        Toast.Type.WARNING,
        'Generating Report PDF'
      );
    }, 5000);
    setProcessingPDF(true);
    try {
      const buf = await api.renderPdf({
        name: reportTypes?.[reportType]?.[0] || 'Report',
        orientation: pdfOrientation,
        header,
        footer,
        tables,
        filters,
        type: reportType as ReportTypeKeys,
      });
      const blob = new Blob([buf], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      saveFile(url, `${pascalCase(reportType)}-${new Datetime().format(DATE_INPUT_FORMAT)}.pdf`);
    } catch (error) {
      console.error('Failed to generate PDF', error);
      createNotification('Failed to create a PDF of this report', Toast.Type.DANGER, 'Report PDF');
    } finally {
      clearTimeout(timer);
      setProcessingPDF(false);
    }
  }, [api, reportType, pdfOrientation, header, footer, tables, filters]);

  const getReport = async (values: any): Promise<void> => {
    const payload: any = { snapshotIds: id };
    // Generate payload.
    Object.entries(values)
      .filter(([key]: [string, unknown]): boolean => reportQueryInputKeys.includes(key))
      .forEach(([key, value]: any): void => {
        payload[key] = value || null;
      });
    const fn = stringify.compare(lastQuery.current, payload) ? refetch : fetch;
    const res = await fn(payload);
    lastQuery.current = payload;
    return res;
  };

  const downloadTrainingReportPdf = async (): Promise<void> => {
    setProcessingPDF(true);
    try {
      await generateAndDownloadTrainingReportPDF({ ...lastQuery.current, ...filtersValues });
    } catch (err) {
      createNotification(err?.message || err, Toast.Type.DANGER, 'PDF Generation Failed');
    } finally {
      setProcessingPDF(false);
    }
  };

  const getCsv = async (): Promise<void> => {
    const payload: any = lastQuery.current;
    const response = await reportQueryAsCsv(reportType)(payload);
    if (!response) return;
    const blob = new Blob([response], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    saveFile(url, `${pascalCase(reportType)}-${new Datetime().format(DATE_INPUT_FORMAT)}.csv`);
  };

  const toggleSnapshotModal = (): void =>
    setState((current: DynamicReportState): DynamicReportState => ({ ...current, showSnapshots: !current?.showSnapshots }));

  const filtersValues = Object.entries(filters).reduce((acc, [key, val]: [string, Record<string, unknown>]) => {
    if (val?.value) acc[key] = val.value;
    return acc;
  }, {});

  useEffect(() => {
    lastQuery.current = {};
    lastReportType.current = reportType;
  }, [reportType]);

  return (
    <div className={`d-flex flex-column ${!isWidget ? 'h-100' : ''}`}>
      <Filters
        name={!isWidget ? `${reportType}Filters` : undefined}
        onSubmit={getReport}
        onReset={(): {} => ({})}
        value={filtersValues}
        primary={({ values = {}, onChange }: any): JSX.Element => (
          <>
            {Object.entries(filters)
              .filter(([key, _value]: [string, any]): boolean => Object.keys(reportFilters).includes(key))
              .slice(0, 6)
              .map(([key]: any, f: number): JSX.Element => {
                const dateRangeValues = [
                  values?.startDatetime || values?.startDatetime?.value?.[0],
                  values?.endDatetime || values?.endDatetime?.value?.[0],
                ].flat();
                if (key === 'endDatetime' && Object.keys(filters).includes('startDatetime')) return null;
                if (key === 'startDatetime' && Object.keys(filters).includes('endDatetime')) key = 'dateRange';
                const Component = reportFilters[key];
                return (
                  <Component
                    name={key}
                    value={values?.[key]}
                    values={dateRangeValues}
                    onChange={onChange}
                    filter={
                      key === 'groupBy' || key === 'complexFilters'
                        ? output?.filters?.find(({ key: filterKey }: any): boolean => filterKey === key)?.options
                        : undefined
                    }
                    report={values}
                    key={f}
                  />
                );
              })}
          </>
        )}
        secondary={
          Object.keys(filters).filter((key: string): boolean => Object.keys(reportFilters).includes(key)).length >= 7
            ? ({ values = {}, onChange }: any): JSX.Element => (
                <>
                  {Object.entries(filters)
                    .filter(([key, _value]: [string, any]): boolean => Object.keys(reportFilters).includes(key))
                    .slice(6)
                    .map(([key]: any, f: number): JSX.Element => {
                      const Component = reportFilters[key];
                      return (
                        <Component
                          name={key}
                          value={values?.[key]}
                          values={[
                            values?.startDatetime?.value || values?.startDatetime,
                            values?.endDatetime?.value || values?.endDatetime,
                          ].flat()}
                          onChange={onChange}
                          filter={
                            key === 'groupBy' || key === 'complexFilters'
                              ? output?.filters?.find(({ key: filterKey }: any): boolean => filterKey === key)?.options
                              : undefined
                          }
                          report={values}
                          key={f}
                        />
                      );
                    })}
                </>
              )
            : undefined
        }
        alternate={(): JSX.Element => (
          <>
            {!report && (
              <ReportTypeDropdown
                ignore={reportType === 'clientSummaryReportSnapshot' ? [window.location.pathname] : []}
                options={{
                  locale: {
                    'Select...': reportType === 'clientSummaryReportSnapshot' ? 'Report Snapshot' : 'Select...',
                  },
                }}
              />
            )}
          </>
        )}
        submitOnMount={lastReportType.current !== reportType}
        noPortal={isWidget}
        key={`${reportType}-${key}`}
      />
      <div className={`Report ${reportType} py-4 flex-grow-1`}>
        <LoadingBlur fitViewport loading={loading} />
        {error && (
          <div className="pt-5">
            <Middle.Center>
              <Alert className="text-center" variant="danger">
                <h5>
                  Something went wrong.
                  <small className="d-block mt-3">
                    <em>{error?.message || error}</em>
                  </small>
                </h5>
              </Alert>
            </Middle.Center>
          </div>
        )}
        {!error && (
          <ReportDisplay
            header={header}
            tables={tables}
            footer={footer}
            filters={filters}
            onDetails={(detailsObj: Record<string, unknown>): void =>
              reportType === 'trainingReport'
                ? openEvaluation({ attemptId: detailsObj?.attemptId as string | undefined })
                : setState((current: DynamicReportState): DynamicReportState => ({ ...current, details: detailsObj }))
            }
            details={details}
            onEmpty={<OnEmpty />}
            as={display}
            loading={loading}
            type={reportType}
          />
        )}
        {!isWidget && (
          <>
            <SidebarButton name="EXPORT_CSV" tooltip="Export as CSV" disabled={loading} onClick={getCsv}>
              <i className="sv sv-excel" />
            </SidebarButton>
            <SidebarButton name="PRINT_SCREEN" tooltip="Print Report" disabled={loading} onClick={printScreen}>
              <i className="sv sv-print" />
            </SidebarButton>
            <SidebarButton
              tooltip={display === ReportDisplayEnum.TABLE ? 'Switch to Chart' : 'Switch to Table'}
              disabled={loading}
              onClick={() =>
                setState(
                  (current: DynamicReportState): DynamicReportState => ({
                    ...current,
                    display: display === ReportDisplayEnum.TABLE ? ReportDisplayEnum.CHART : ReportDisplayEnum.TABLE,
                  })
                )
              }
            >
              <i className={`sv sv-${display === ReportDisplayEnum.TABLE ? 'chart-growth' : 'grid'}`} />
            </SidebarButton>
            {reportType === 'clientSummaryReport' && (
              <SidebarButton name="SNAPHOTS" tooltip="Snapshots" onClick={toggleSnapshotModal}>
                <i className="sv sv-camera2" />
              </SidebarButton>
            )}
            {!loading && tables.length > 0 && reportType === 'trainingReport' && (
              <SidebarButton name="PDF" tooltip="Download Report as PDF" onClick={downloadTrainingReportPdf} disabled={processingPDF}>
                {!processingPDF && <i className="sv sv-download2" />}
                {processingPDF && <i className="fa fa-spinner fa-pulse" />}
              </SidebarButton>
            )}
            {!loading && tables.length > 0 && allowedPDFReports.includes(reportType as ReportTypeKeys) && (
              <SidebarButton name="PDF" tooltip="Download Report as PDF" onClick={handleDownloadReportPDF} disabled={processingPDF}>
                {!processingPDF && <i className="sv sv-download2" />}
                {processingPDF && <i className="fa fa-spinner fa-pulse" />}
              </SidebarButton>
            )}
          </>
        )}
      </div>
      {reportType === 'trainingReport' ? (
        // Training-report rows drill into the read-only Evaluation Summary (store-driven, keyed by the
        // row's hidden `attemptId`), never the generic detailed-report drawer.
        <EvaluationSummaryModal />
      ) : (
        <DetailsDrawer
          className="ReportDetails"
          show={details}
          onClose={(): void => setState((current: DynamicReportState): DynamicReportState => ({ ...current, details: undefined }))}
          report={report}
          query={lastQuery.current}
        />
      )}
      <SnapshotsModal show={showSnapshots} onHide={toggleSnapshotModal} />
    </div>
  );
};

export default DynamicReport;
